import React, {FC} from 'react'

/**
 * Normalizes any phone number into E.164 call format (e.g. +79108870609)
 * for use in `tel:` links.
 *
 * Examples:
 * - '9108870609' -> '+79108870609'
 * - '8 (910) 8870609' -> '+79108870609'
 * - '+7 (910) 887-06-09' -> '+79108870609'
 * - '79108870609' -> '+79108870609'
 * - '+375 (29) 123-45-67' -> '+375291234567'
 */
export function formatPhoneForCall(phone: string | number | null | undefined): string {
  if (!phone) return ''
  const str = String(phone).trim()
  if (!str) return ''

  const hasPlus = str.startsWith('+')
  const digits = str.replace(/\D/g, '')
  if (!digits || digits.length < 6) return ''

  // Already has explicit international '+' prefix
  if (hasPlus) {
    return `+${digits}`
  }

  // Russian 11 digits starting with 8: 89108870609 -> +79108870609
  if (digits.length === 11 && digits.startsWith('8')) {
    return `+7${digits.slice(1)}`
  }

  // Russian 11 digits starting with 7: 79108870609 -> +79108870609
  if (digits.length === 11 && digits.startsWith('7')) {
    return `+${digits}`
  }

  // Russian 10 digits starting with 9: 9108870609 -> +79108870609
  if (digits.length === 10 && digits.startsWith('9')) {
    return `+7${digits}`
  }

  // General fallback: if 10 or more digits, prepend '+'
  if (digits.length >= 10) {
    return `+${digits}`
  }

  return digits
}

/**
 * Formats raw unformatted phone numbers into readable form `+7 (XXX) XXX-XX-XX`.
 * If phone already contains formatting (spaces, brackets, dashes), it is preserved.
 */
export function formatPhoneDisplay(phone: string | number | null | undefined): string {
  if (!phone) return ''
  const str = String(phone).trim()
  if (!str) return ''

  const digits = str.replace(/\D/g, '')
  if (digits.length < 6) return ''

  // If already formatted, keep original
  if (/[ ()\-]/.test(str)) {
    if (str.includes('_')) return ''
    return str
  }

  // 10 digits starting with 9
  if (digits.length === 10 && digits.startsWith('9')) {
    return `+7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8, 10)}`
  }

  // 11 digits starting with 8 or 7
  if (digits.length === 11 && (digits.startsWith('8') || digits.startsWith('7'))) {
    return `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`
  }

  return str
}

export interface PhoneLinkProps {
  phone: string | number | null | undefined
  className?: string
  style?: React.CSSProperties
  children?: React.ReactNode
}

/**
 * Clickable phone link component that dials formatted phone on mobile/desktop
 */
export const PhoneLink: FC<PhoneLinkProps> = ({phone, className, style, children}) => {
  if (!phone) return null
  const tel = formatPhoneForCall(phone)
  if (!tel) return <>{phone}</>

  const displayText = children || formatPhoneDisplay(phone)

  return (
    <a
      href={`tel:${tel}`}
      className={className}
      style={{
        color: 'inherit',
        textDecoration: 'underline',
        textUnderlineOffset: '2px',
        cursor: 'pointer',
        ...style,
      }}
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      {displayText}
    </a>
  )
}
